import { FC, useMemo } from "react";
import { Box, Grid, Popover, Stack, Typography, styled } from "@mui/material";
import { background, black, divider, text } from "@/theme/colors";
import { IconBox } from "@/components/elements/IconBox";
import { IconClose } from "@/assets/icons";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { TOption } from "@/types/common";
import { EMPTY_STR } from "@/constants/string";
import {
  TCaseListFilterFormExtended,
  CASE_LIST_FILTER_FIELD_LABELS,
  CASE_DATE_RANGE_FIELD,
  formatCaseDateValue,
} from "@/features/case/components/CaseList/case-list-filter-shared";
import { useCaseListFilterMasterData } from "@/features/case/components/CaseList/use-case-list-filter-master-data";
import { findLabelByValue } from "@/utils/object";

type CaseListAppliedConditionDetailPopoverProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  filterValues: TCaseListFilterFormExtended | null;
};

const resolveLabel = (value: unknown, options: TOption[]): string => {
  // Handle multi-select array values
  if (Array.isArray(value)) {
    if (value.length === 0) return EMPTY_STR.TEXT;
    const labels = value
      .map((v) => options.find((opt) => String(opt.value) === String(v))?.label)
      .filter(Boolean) as string[];
    return labels.length > 0 ? labels.join(", ") : EMPTY_STR.TEXT;
  }
  if (value === null || value === undefined || value === "" || value === 0) return EMPTY_STR.TEXT;
  const found = options.find((opt) => String(opt.value) === String(value));
  return found ? found.label : String(value);
};

export const CaseListAppliedConditionDetailPopover: FC<CaseListAppliedConditionDetailPopoverProps> = ({ anchorEl, onClose, filterValues }) => {
  const open = Boolean(anchorEl);
  const { rawOptions, getMunicipalityOptionsByProvince } = useCaseListFilterMasterData();

  const optionsMap = useMemo(
    (): Partial<Record<keyof TCaseListFilterFormExtended, TOption[]>> => ({
      caseStatus: rawOptions.caseStatus,
      companyName: rawOptions.companyName,
      companySpecificIndustryId: rawOptions.industry,
      personInChargeId: rawOptions.personInCharge,
      workplaceProvinceCode: rawOptions.workplaceProvince,
      recruitmentNumber: rawOptions.countRange,
      caseMatchCount: rawOptions.countRange,
    }),
    [rawOptions],
  );

  const getDisplayValue = (key: keyof TCaseListFilterFormExtended): string => {
    if (!filterValues) return EMPTY_STR.TEXT;
    const value = filterValues[key];

    // Municipality options = union from all selected provinces
    if (key === "workplaceMunicipalityCode") {
      const provinceCodes = filterValues.workplaceProvinceCode ?? [];
      if (provinceCodes.length === 0 || !value) return EMPTY_STR.TEXT;
      const seen = new Set<string>();
      const unionedOptions: TOption[] = [];
      for (const code of provinceCodes) {
        for (const opt of getMunicipalityOptionsByProvince(code)) {
          const key2 = String(opt.value);
          if (seen.has(key2)) continue;
          seen.add(key2);
          unionedOptions.push(opt);
        }
      }
      if (Array.isArray(value)) {
        if (value.length === 0) return EMPTY_STR.TEXT;
        const labels = value
          .map((v) => findLabelByValue(unionedOptions, v))
          .filter(Boolean) as string[];
        return labels.length > 0 ? labels.join(", ") : EMPTY_STR.TEXT;
      }
      return findLabelByValue(unionedOptions, value) || EMPTY_STR.TEXT;
    }

    const options = optionsMap[key];
    if (options) return resolveLabel(value, options);
    if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : EMPTY_STR.TEXT;
    if (value === null || value === undefined || value === "") return EMPTY_STR.TEXT;
    return String(value);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{
        "& .MuiPaper-root": {
          mt: 1,
          p: 0,
          borderRadius: "8px",
          boxShadow: "0px 8px 24px 0px #00000026",
        },
      }}
    >
      <PopoverContainer>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoOutlinedIcon sx={{ color: black[80], fontSize: 20 }} />
            <Typography variant="sub16Bold">設定した条件</Typography>
          </Stack>
          <IconBox onClick={onClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={20} />
          </IconBox>
        </Stack>

        <Grid container spacing={3}>
          {CASE_LIST_FILTER_FIELD_LABELS.map(({ key, label }) => (
            <Grid item xs={4} key={key}>
              <Stack spacing={0.5}>
                <Typography variant="cap12">{label}</Typography>
                <Divider />
                <Typography variant="body14" sx={{ color: text.primary }}>
                  {getDisplayValue(key)}
                </Typography>
              </Stack>
            </Grid>
          ))}

          <Grid item xs={4}>
            <Stack spacing={0.5}>
              <Typography variant="cap12">{CASE_DATE_RANGE_FIELD.label}</Typography>
              <Divider />
              <Typography variant="body14" sx={{ color: text.primary }}>
                {filterValues ? formatCaseDateValue(filterValues[CASE_DATE_RANGE_FIELD.fromKey]) : EMPTY_STR.DATE} 〜{" "}
                {filterValues ? formatCaseDateValue(filterValues[CASE_DATE_RANGE_FIELD.toKey]) : EMPTY_STR.DATE}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </PopoverContainer>
    </Popover>
  );
};

const PopoverContainer = styled(Box)`
  width: 780px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  padding: 24px;
  background-color: ${background.white};
`;

const Divider = styled(Box)`
  border-bottom: 1px solid ${divider.middle};
`;
